import { FlexBoxView, FlexBoxViewOpts } from '@play-co/timestep-core/flexbox';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import { openGachaSelectionPopup } from 'src/game/gacha/gacha.sequence';
import StateObserver, { user, now } from 'src/StateObserver';
import { getCurrentGachaEvent } from 'src/replicant/gacha/gacha.getters';
import { formatDuration } from 'src/replicant/utils/time';
import { isDecorationGachaEventAvailable } from './helpers';
import createIntervalEmitter from 'src/lib/createIntervalEmitter';
import { gachaTheme } from '../gacha/gacha.theme';

type GachaButtonOpts = FlexBoxViewOpts & {
  ignoreThemeOffsets?: boolean;
};

export class GachaButton extends FlexBoxView {
  private button: ButtonScaleView;
  private ignoreThemeOffsets: boolean;

  constructor(opts?: GachaButtonOpts) {
    const { ignoreThemeOffsets, ...viewOpts } = opts ?? {};
    super({
      ...viewOpts,
      width: 250,
      height: 210,
      flex: {
        position: 'absolute',
        bottom: 10,
        right: 10,
      },
      visible: isDecorationGachaEventAvailable(),
    });

    this.ignoreThemeOffsets = ignoreThemeOffsets ?? false;

    this.button = new ButtonScaleView({
      superview: this,
      width: 250,
      height: 210,
      pressedOffsetY: 5,
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.FOOTER_BUTTON,
      },
      onClick: async () => {
        if (!isDecorationGachaEventAvailable()) {
          this.updateOpts({
            visible: false,
          });
          return;
        }

        sounds.sfx.play('ok');

        await openGachaSelectionPopup();
      },
    });

    const view = new ImageScaleView({
      superview: this.button,
      width: 254,
      height: 205,
    });

    const timerBg = new ImageScaleView({
      superview: view,
      x: 254 / 2,
      y: 205 - 12,
      width: 225,
      height: 33,
      centerOnOrigin: true,
    });

    const timer = new LangBitmapFontTextView({
      superview: timerBg,
      width: 210,
      height: 33,
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
      color: '#ffffff',
      size: 22,
      localeText: () => '',
      centerOnOrigin: true,
      x: timerBg.style.width / 2,
      y: timerBg.style.height / 2,
    });

    const emitter = createIntervalEmitter((_, now) => ({
      available: isDecorationGachaEventAvailable(),
      now,
    }));

    emitter.addListener(({ available, now }) => {
      const currentEvent = getCurrentGachaEvent(user(), now);

      if (available && currentEvent) {
        timer.localeText = () =>
          i18n('gacha.timer', {
            time: formatDuration(currentEvent.end - now),
          });

        const locale = StateObserver.getLocale();
        const style = gachaTheme().getStyle(`gacha_button_${locale}`);
        view.updateOpts({
          ...style,
          // Strip offsets from the image; they're applied to the container below.
          offsetX: 0,
          offsetY: 0,
        });

        if (this.ignoreThemeOffsets) {
          // Anchor the image to the bottom-right of the container so the
          // visual position matches regardless of the themed image size.
          view.updateOpts({
            x: this.button.style.width - (view.style.width ?? 0) - 10,
            y: this.button.style.height - (view.style.height ?? 0),
          });
        } else {
          // Update the container style to match the image
          this.updateOpts({
            // Make it the same size as the image, so the clickable area is correct
            width: view.style.width ?? this.style.width,
            height: view.style.height ?? this.style.height,
            // Apply the offsets to the container
            offsetX: style.offsetX ?? this.style.offsetX,
            offsetY: style.offsetY ?? this.style.offsetY,
          });

          // Update the button size to match the image
          this.button.updateOpts({
            width: view.style.width ?? this.button.style.width,
            height: view.style.height ?? this.button.style.height,
          });
        }

        timerBg.updateOpts({
          ...gachaTheme().getStyle('timer_bg'),
        });
      }
      this.updateOpts({
        visible: available,
      });
    });

    this.on('ViewRemoved', () => {
      emitter.removeAllListeners();
    });
  }
}
